import { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { notification } from 'antd';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import { getSalesforceIdForAiCache, saveAIResult, saveLocalAiResultCache } from '../../../../../services/geminiService';
import { logger } from '../../../../../services/loggingService';
import { AlertType } from '../../../../../@types/enums';
import {
    buildLocalAiResultCachePayloadFromStore,
    buildSaveAiResultPayloadFromStore,
} from '../utils/aiLayoutCacheHydration';

interface UseSaveAiScenariosArgs {
    sceneViewerProp: any;
}

function showDashboardSaveAlert(title: string, message: string, messageType: AlertType): void {
    const theme = useOneButtonStore.getState().theme ?? 'light';
    const className = `obd-notification obd-theme-${theme}${
        messageType === AlertType.error ? ' obd-notification-error' : ''
    }`;
    const payload = {
        message: title,
        description: message,
        placement: 'topRight' as const,
        className,
        duration: messageType === AlertType.success ? 6 : 8,
    };

    if (messageType === AlertType.success) {
        notification.success(payload);
        return;
    }
    if (messageType === AlertType.error) {
        notification.error(payload);
        return;
    }
    notification.info(payload);
}

function dismissSaveProgressToast(
    setSavingScenarios: (next: boolean) => void,
    setAiSavingProgress: (next: number) => void,
): void {
    flushSync(() => {
        setSavingScenarios(false);
        setAiSavingProgress(0);
    });
}

export function useSaveAiScenarios({
    sceneViewerProp: _sceneViewerProp,
}: UseSaveAiScenariosArgs) {
    const [savingScenarios, setSavingScenarios] = useState(false);
    const [aiSavingProgress, setAiSavingProgress] = useState(0);
    const saveInFlightRef = useRef(false);

    const handleSaveAiScenarios = useCallback(async (includeSnapshotsOnSave: boolean) => {
        if (saveInFlightRef.current) return;
        saveInFlightRef.current = true;
        setSavingScenarios(true);
        setAiSavingProgress(0);
        try {
            const payload = await buildSaveAiResultPayloadFromStore(
                includeSnapshotsOnSave,
                includeSnapshotsOnSave
                    ? (completed, total) => {
                          if (total > 0) {
                              const compressPercent = Math.round((completed / total) * 30);
                              setAiSavingProgress(Math.min(30, Math.max(0, compressPercent)));
                          }
                      }
                    : undefined,
            );
            const res = await saveAIResult(payload, (progress) => {
                if (!Number.isFinite(progress)) return;
                if (progress >= 100) {
                    setAiSavingProgress(100);
                    return;
                }
                const uploadFloor = includeSnapshotsOnSave ? 30 : 0;
                const uploadPercent = uploadFloor + Math.round((progress / 99) * (99 - uploadFloor));
                setAiSavingProgress(Math.min(99, Math.max(uploadFloor, uploadPercent)));
            });
            dismissSaveProgressToast(setSavingScenarios, setAiSavingProgress);
            if (res) {
                const sfid = getSalesforceIdForAiCache();
                if (sfid) {
                    void (async () => {
                        try {
                            const cachePayload = await buildLocalAiResultCachePayloadFromStore(
                                useOneButtonStore.getState().savedAIDesigns ?? [],
                            );
                            await saveLocalAiResultCache(cachePayload, sfid, { force: true });
                        } catch (cacheError) {
                            logger.warn('Local AI result cache update failed after save', cacheError);
                        }
                    })();
                }
            }
            showDashboardSaveAlert(
                res ? 'Saved' : 'Save failed',
                res ? 'AI scenarios were saved to this design version.' : 'Unable to save AI scenarios.',
                res ? AlertType.success : AlertType.error,
            );
        } catch (error) {
            logger.error('Save AI scenarios failed', error);
            const detail = error instanceof Error ? error.message : '';
            showDashboardSaveAlert(
                'Save failed',
                detail || 'Unable to save AI scenarios.',
                AlertType.error,
            );
        } finally {
            saveInFlightRef.current = false;
            dismissSaveProgressToast(setSavingScenarios, setAiSavingProgress);
        }
    }, []);

    return { savingScenarios, aiSavingProgress, handleSaveAiScenarios };
}
